import type {
  AddOnOption,
  EventStayPeriod,
  FlightSegment,
  PaymentOption,
} from "@/lib/api/types"
import type { BookingState } from "@/types/booking"

export function getSelectedStayPeriod(
  state: BookingState
): EventStayPeriod | null {
  if (!state.event || state.selectedStayPeriodIndex === null) return null

  return state.event.stayPeriods[state.selectedStayPeriodIndex] ?? null
}

export function getAvailableAddOns(state: BookingState): AddOnOption[] {
  const addOns = [
    ...(state.event?.addOns ?? []),
    ...(state.packageType !== "flight-only"
      ? (state.hotelAddOns[state.selectedHotel?.hotelId ?? ""] ?? [])
      : []),
  ]

  const seen = new Set<string>()

  return addOns.filter((addOn) => {
    if (seen.has(addOn.id)) return false
    seen.add(addOn.id)
    return true
  })
}

export function isTransferAddOn(state: BookingState, addOnId: string): boolean {
  return getAvailableAddOns(state).some(
    (a) => a.id === addOnId && (a.type ?? "").toLowerCase().includes("transfer")
  )
}

export function addOnQuantityLimit(state: BookingState, addOnId: string): number {
  if (isTransferAddOn(state, addOnId)) return 99
  return state.passengerCount
}

export function getSelectedAddOns(state: BookingState) {
  const addOnsById = new Map(
    getAvailableAddOns(state).map((addOn) => [addOn.id, addOn])
  )

  return Object.entries(state.selectedAddOns)
    .filter(([, quantity]) => quantity > 0)
    .flatMap(([addOnId, quantity]) => {
      const addOn = addOnsById.get(addOnId)
      return addOn ? [{ addOn, quantity }] : []
    })
}

export function getPackagePricePerPerson(state: BookingState) {
  if (state.packageType === "flight-only") {
    const base = state.flightBasePackagePrice ?? state.offers?.basePackagePricePerPerson ?? 0
    return Math.max(
      base + (state.selectedFlight?.priceDiffPerPerson ?? 0),
      0
    )
  }

  if (!state.offers || !state.selectedHotel) return 0

  return Math.max(
    state.offers.basePackagePricePerPerson +
      state.selectedHotel.priceDiffPerPerson +
      (state.packageType === "flight-hotel"
        ? (state.selectedFlight?.priceDiffPerPerson ?? 0)
        : 0),
    0
  )
}

export function getAddOnsTotal(state: BookingState) {
  return getSelectedAddOns(state).reduce(
    (total, { addOn, quantity }) =>
      total + addOn.priceDeltaPerPerson * quantity,
    0
  )
}

export function getGrandTotal(state: BookingState) {
  return (
    getPackagePricePerPerson(state) * state.passengerCount +
    getAddOnsTotal(state)
  )
}

export function getStayPeriodLabel(
  stayStartDate: string,
  stayEndDate: string,
  eventStartDate: string | null | undefined,
  eventEndDate: string | null | undefined,
  allPeriods?: { stayStartDate: string; stayEndDate: string }[]
): "most-popular" | "full-event-stay" | "arrive-early" | "leave-late" | "flexible-dates" | null {
  if (!eventStartDate || !eventEndDate) return null

  const sStart = stayStartDate.slice(0, 10)
  const sEnd = stayEndDate.slice(0, 10)
  const eStart = eventStartDate.slice(0, 10)
  const eEnd = eventEndDate.slice(0, 10)

  const startMatch = sStart === eStart
  const endMatch = sEnd === eEnd
  const coversEvent = sStart <= eStart && sEnd >= eEnd

  if (startMatch && endMatch) return "most-popular"

  const hasExactMatch = allPeriods?.some((p) => {
    return p.stayStartDate.slice(0, 10) === eStart && p.stayEndDate.slice(0, 10) === eEnd
  }) ?? false

  const nextDay = new Date(`${eEnd}T12:00:00Z`)
  nextDay.setUTCDate(nextDay.getUTCDate() + 1)
  const nextDayAfterEvent = nextDay.toISOString().slice(0, 10)

  if (!hasExactMatch && startMatch && sEnd === nextDayAfterEvent) return "full-event-stay"
  if (coversEvent && !hasExactMatch) return "most-popular"
  if (!startMatch && sStart < eStart && endMatch) return "arrive-early"
  if (startMatch && !endMatch && sEnd > eEnd) return "leave-late"
  if (coversEvent && hasExactMatch && sStart < eStart && sEnd > eEnd) return "full-event-stay"

  return "flexible-dates"
}

export function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export function formatMoney(value: number, currency: string) {
  const formatted = Number.isInteger(value)
    ? value.toFixed(0)
    : value.toFixed(2)
  return `${formatted.replace(".", ",")} ${currency}`
}

export function formatPriceDelta(
  value: number,
  currency: string,
  showZeroAsDiff = false
) {
  if (value === 0)
    return showZeroAsDiff
      ? `+${formatMoney(0, currency)}`
      : `Included in package`
  if (value > 0) return `+${formatMoney(value, currency)}`
  return `-${formatMoney(Math.abs(value), currency)}`
}

export function formatDateLabel(date: string | null | undefined) {
  if (!date) return "TBA"

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

export function formatDayLabel(date: string | null | undefined) {
  if (!date) return ""

  return new Date(date)
    .toLocaleDateString("en-US", { weekday: "short" })
    .toUpperCase()
}

export function dayOffset(segment: FlightSegment): number {
  return Math.round(
    (Date.parse(segment.arrivalDate) - Date.parse(segment.departureDate)) /
      86_400_000
  )
}

export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  if (!startDate || !endDate) return "Dates available on request"

  const start = new Date(startDate)
  const end = new Date(endDate)
  const startMonth = start.toLocaleDateString("en-US", { month: "long" }).toUpperCase()
  const endMonth = end.toLocaleDateString("en-US", { month: "long" }).toUpperCase()
  const startDay = start.getDate()
  const endDay = end.getDate()
  const year = end.getFullYear()

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} – ${endDay}, ${year}`
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`
}

export function getNightCount(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  if (!startDate || !endDate) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)
  return Math.max(
    Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
    0
  )
}

export function getActivePaymentOption(
  state: BookingState,
  payLaterEnabled: boolean
): PaymentOption {
  if (payLaterEnabled && state.offers?.payLater) {
    return state.offers.payLater.option
  }

  return "FULL"
}

export function getDestinationAirportCode(state: BookingState): string | null {
  return state.destinationAirport?.code ?? state.event?.airport ?? null
}
